/**
 * @voltxampere/components — LED dedicated component (Sprint 23 B1)
 *
 * Sprint 22'ye kadar LED, `SCHEMA_SYMBOL_ALIASES` üzerinden diode
 * stamp'ına yönlendiriliyordu (Vf bilgisi yoktu, kullanıcı `Is`/`N`
 * default'ları silikon Si diyot için ayarlanmış 1e-14 / 1.0
 * değerleriyle çalışıyordu — 0.7 V bias). Sprint 23 B1 LED'i
 * dedicated component'a yükseltir; LED-spesifik default'lar
 * (Vf ≈ 2 V kırmızı LED için Is=1e-12, n=2) + opsiyonel `color`
 * + opsiyonel `forwardVoltage` informational hint.
 *
 * Sprint 22 D2'nin junction-cap parametreleri (Cj0, Vj, M, Tt, FC)
 * LED için de geçerli (paket-bağımsız diyot fiziği). Optional
 * + zero-default — Sprint 5/6/7/20/22 LED-içermeyen fixture'larını
 * etkilemez.
 *
 * `forwardVoltage` UI içindir (parametre tablosunda varsayılan
 * Vf hint'i olarak gösterilir); engine stamp'ı `Is` + `n` üzerinden
 * çalışır. UI Sprint 23 D3 inspector "select" tipi ile preset
 * dropdown'u (Red/Green/Blue/Yellow/White) Sprint 24+ scope'unda.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
export const LedColorSchema = z.enum(["red", "green", "blue", "yellow", "white"]);
export const LedParamsSchema = z
    .object({
    /**
     * Saturation current `Is` (A). LED varsayılanı 1e-12 + n=3 birleşimi
     * kırmızı LED'i (Vf ≈ 1.8 V at 15 mA, 5 V supply + 220 Ω serisinde
     * Vf ≈ 1.82 V) sayısal olarak doğru fit eder. Mavi LED için 1e-15,
     * yeşil LED için 1e-13 önerilir; kullanıcı override eder.
     */
    saturationCurrent: z.number().positive().finite().default(1e-12),
    /**
     * Ideality factor `n`. LED varsayılanı 3 — silikon diyot ideal
     * Shockley'den (n=1) sapma. Yeşil LED'ler n ≈ 2.5, mavi LED'ler
     * n ≈ 4'e kadar çıkabilir.
     */
    ideality: z.number().positive().finite().default(3),
    /** Optional series resistance for non-ideal LEDs. */
    seriesResistance: z.number().nonnegative().finite().optional(),
    /**
     * Forward voltage hint (informational). Mantığı UI param tablosunda
     * göstermek için — engine stamp'ı `Is` + `n`'i kullanır. Default
     * 2.0 V (kırmızı LED).
     */
    forwardVoltage: z.number().positive().finite().default(2.0),
    /** LED color (UI rendering hint, schematic glow color). */
    color: LedColorSchema.default("red"),
    /** Sprint 22 D2 junction-cap parameters (zero-default, additive). */
    Cj0: z.number().nonnegative().finite().optional(),
    Vj: z.number().positive().finite().optional(),
    M: z.number().positive().finite().optional(),
    Tt: z.number().nonnegative().finite().optional(),
    FC: z.number().min(0).lt(1).optional(),
})
    .strict();
export const ledDefinition = {
    type: "led",
    category: "active",
    displayName: { tr: "LED", en: "LED" },
    symbol: "LED",
    pins: [
        { id: "anode", side: "left", offset: 0, label: "A" },
        { id: "cathode", side: "right", offset: 0, label: "K" },
    ],
    parameterSchema: LedParamsSchema,
    nonlinear: true,
};
//# sourceMappingURL=led.js.map